/* components/SiteHeader.ts
Global Site Header

Example
<site-header>
    Site header content here
</site-header>
*/

import { Component, Ref, Prop, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { debounce } from 'lodash';
import { CartMain } from "../Cart/CartMain";
import { PredictiveSearch } from '../PredictiveSearch/PredictiveSearch';
import { SideCart } from '../SideCart/SideCart';

declare global {
    interface Window {
        // eslint-disable-next-line
        _swat?: any;
    }
}

@Component
export class SiteHeader extends ThemeComponent {
    @Prop() wishlistEnabled!: 'true'|'false';
    @Ref() row!: HTMLDivElement;
    @Ref({required:false}) close!: HTMLButtonElement[];
    @Ref() rowHeight!: HTMLDivElement;
    @Ref() count!: HTMLElement;
    @Ref() menuToggle!: HTMLButtonElement;
    @Ref({required:false}) wishlist!: HTMLElement;
    @Ref({required:false}) searchTrigger!: HTMLButtonElement;
    @Ref({required:false}) searchRow!: HTMLElement;

    private cartMain:CartMain = document.body.querySelector('cart-main') as CartMain;
    private sideCart:SideCart|null = document.body.querySelector('side-cart');
    private _onSearchClose = new SimpleEvent<boolean>();
    public get onSearchClose() {
        return this._onSearchClose.asEvent();
    }

    /**
     * Initialise
     */
    init() {
        this.handleBodyPadding();
        this.$subscribe(this.cartMain.onCartItemCountChange, debounce((value) => {
            this.updateCartCount(value);
        }, 300));

        this.cartEvent();
        this.setEventListeners();

        // Wait for Wishlist to load
        setTimeout(()=> {
            this.handleSwymObserver();
            this.updateWishlistCount();
        }, 1500);
    }

    /**
     * Set event listeners.
     */
    setEventListeners() {
        this.$listen(window, 'resize',
            debounce(() => {
                this.handleBodyPadding();
            }, 300)
        );

        for (const close of this.close) {
            this.$listen(close, 'click', () => {
                close.blur();
                this.handleSearch('close');
            });
        }

        this.$listen(this.menuToggle, 'click', () => {
            this.handleDrawerOpen();
        });

        if (this.searchTrigger) {
            this.$listen(this.searchTrigger, 'click', () => {
                this.handleSearch('open');
            });
        }

        document.addEventListener('wishlistUpdated', () => {
            this.updateWishlistCount();
        });

        if (!this.searchRow) { return; }
        const predictiveSearch:PredictiveSearch|null = this.searchRow. querySelector('predictive-search');
        if (predictiveSearch) {
            this.$subscribe(predictiveSearch.onSearchInput, () => this.handleSearch('open'));
        }
    }

    /**
     * Handle Body Padding.
     */
    private handleBodyPadding() {
        const heightRow = this.rowHeight.offsetHeight;
        document.body.style.paddingTop = `${heightRow}px`;
    }

    /**
     * Cart Icon click event.
     * @returns
     */
    cartEvent() {
        const cartTrigger: HTMLElement|null = this.querySelector('[data-ref="cartTrigger"]');
        if (!cartTrigger) { return; }

        this.$listen(cartTrigger, 'click', (event) => {
            event.preventDefault();
            this.handleSearch('close');

            if (!this.sideCart) { return; }
            this.sideCart.openCart();
        });
    }

    /**
     * Update cart count
     * @returns
     */
    private updateCartCount(count: number) {
        if(!this.count) { return; }

        this.count.innerHTML = String(count);
        if (count > 0) {
            this.count.parentElement?.classList.add('is-active');
        } else {
            this.count.parentElement?.classList.remove('is-active');
        }
    }

    /**
     *
     * @returns Update wishlist count.
     */
    updateWishlistCount() {
        if (this.wishlistEnabled == 'false') { return; } // wishlist disabled

        /* eslint-disable */
        // swym wishlist specific call format fails ts linting.
        if (!window._swat) { return; }
        const $this = this;

        window._swat.wishlistCount((count:string) => {
            if (~~count <= 0) {
                $this.wishlist.parentElement?.classList.remove('is-active');
                return;
            }
            $this.wishlist.innerHTML = count;
            $this.wishlist.parentElement?.classList.add('is-active');
        });
        /* eslint-enable */
    }

    /**
     * Handdle wishlist modal add to cart.
     * @returns
     */
    handleSwymObserver() {
        const swymModal =  document.querySelector('#swym-plugin');
        if (!swymModal) { return; }

        const mutations = new MutationObserver(() => {
            this.handleWishlistAddToCart(swymModal);
            setTimeout(()=> {
                this.updateWishlistCount();
            }, 1000);
        });
        mutations.observe(swymModal, {
            subtree: true,
            childList: true
        });
    }

    /**
     * Handle add to cart from wishlist modal.
     * @param modal - swym modal.
     */
    handleWishlistAddToCart(modal:Element) {
        const submits = modal.querySelectorAll('.swym-add-to-cart-btn');
        for (const submit of submits) {
            const button = submit as HTMLButtonElement;
            this.$listen(button, 'click', debounce(() => {
                // Wait for addToCart
                setTimeout(()=> {
                    this.cartMain.reloadCart();
                }, 1500);
            },1000));
        }
    }

    /**
     * Trigger search bar.
     * @param action - 'open' / 'close'
     * @returns
     */
    handleSearch(action:string) {
        if (!this.searchRow) { return; }
        const search: HTMLInputElement|null = this.searchRow.querySelector('input[type=search]');

        if (action === 'open') {
            this.searchRow.classList.add('is-active');
            document.body.classList.add('drawer-open');
            search?.focus();
            return;
        }

        this.searchRow.classList.remove('is-active');
        document.body.classList.remove('drawer-open');
        this._onSearchClose.dispatch(true);

        if(!search) { return; }
        search.value = '';
    }

    /**
     * Trigger menu drawer.
     * @returns
     */
    handleDrawerOpen() {
        if (window.innerWidth > 1200) { return; }
        const menu = document.querySelector('.mobile-menu');
        const overlay = document.querySelector('.mobile-menu__overlay');

        this.handleSearch('close');
        document.documentElement.classList.add('drawer-open');
        document.body.classList.add('drawer-open');
        menu?.classList.add('is-active');
        overlay?.classList.add('is-active');
    }
}
